import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LatLngLiteral } from '@agm/core';
import { GISUtils } from '../../core/utils/gis-utils';
import { DatasetService } from '../../data-access-layer/dataset/dataset.service';
import { PRECISELY_CRIME_ARRAY, PRECISELY_WILDFIRE_RISK_ARRAY } from '../../model/datapoint/location-profile/location-profile';

/**
 * Boundary polygons drawn on the map for the selected Location Profile option, keyed by profile.
 *
 * Lives in a service rather than on MapComponent for two reasons. MapComponent is already very
 * large, and this state is written by DatapointsComponent (which handles the dropdown change) while
 * being read by the map template - a shared service is the natural place for that, instead of one
 * more @Input plus a create/remove method pair per source.
 *
 * Keyed by profile so adding a boundary to another profile option is one setPolygon call, not a new
 * field, a new create method and a new remove method as the one-off Precisely COPE polygon
 * needed.
 */
@Injectable({
    providedIn: 'root'
})
export class ProfilePolygonService {

    /** Read by the map template; keys are Location Profile options. */
    polygons: { [profileKey: string]: { paths: LatLngLiteral[] } } = {};

    private readonly zoomRequests = new Subject<string>();

    /** Opt-in: a caller that wants the map moved to its polygon calls requestZoom. */
    get zoomRequested$(): Observable<string> {
        return this.zoomRequests.asObservable();
    }

    requestZoom(profileKey: string) {
        if (this.polygons[profileKey]) {
            this.zoomRequests.next(profileKey);
        }
    }

    private readonly PRECISELY_CRIME = PRECISELY_CRIME_ARRAY[0];
    private readonly PRECISELY_WILDFIRE_RISK = PRECISELY_WILDFIRE_RISK_ARRAY[0];

    constructor(private readonly datasetService: DatasetService) {
    }

    /**
     * Fetch and draw the Census block group boundary behind Precisely Crime.
     *
     * Shared by the datapoint Location Profile and the address profile: the two differ only in
     * where the inputs come from - a datapoint's stored fields versus a searched address's
     * see-info payload - so only the lookup itself lives here.
     *
     * Clears the polygon rather than erroring whenever there is nothing to draw: no identifiers,
     * a location outside the US (block groups are US-only), or a failed request. A map-only
     * problem should not disturb the crime figures the panel was opened for.
     *
     * @param blockGroupCode Precisely's 12-digit Census GEOID; may be null, in which case the
     *                       backend resolves the boundary from the coordinates instead
     * @param onSettled      optional callback once the polygon is set or cleared, for callers
     *                       running OnPush change detection
     */
    loadPreciselyCrimeBoundary(blockGroupCode: string, latitude: number, longitude: number,
                               vintage?: string, onSettled?: () => void) {
        const hasLocation = latitude !== undefined && latitude !== null
            && longitude !== undefined && longitude !== null;

        if (!blockGroupCode && !hasLocation) {
            this.clearPreciselyCrimeBoundary();
            if (onSettled) {
                onSettled();
            }
            return;
        }

        this.datasetService.fetchBlockGroupGeometry(
            blockGroupCode,
            hasLocation ? latitude : undefined,
            hasLocation ? longitude : undefined,
            vintage
        ).subscribe({
            next: (response) => {
                this.setPolygon(this.PRECISELY_CRIME, response && response.found ? response.geometry : null);
                if (onSettled) {
                    onSettled();
                }
            },
            error: () => {
                this.clearPreciselyCrimeBoundary();
                if (onSettled) {
                    onSettled();
                }
            }
        });
    }

    clearPreciselyCrimeBoundary() {
        this.clearPolygon(this.PRECISELY_CRIME);
    }

    clearPreciselyWildfireRiskBoundary() {
        this.clearPolygon(this.PRECISELY_WILDFIRE_RISK);
    }

    /**
     * Draw a GeoJSON Polygon or MultiPolygon for a profile option. A null or unusable geometry
     * clears the polygon instead, so "no boundary for this location" needs no special-casing at
     * the call site.
     */
    setPolygon(profileKey: string, geometry: any) {
        const paths = GISUtils.geoJsonToPolygonPaths(geometry);
        if (!paths) {
            this.clearPolygon(profileKey);
            return;
        }
        this.polygons[profileKey] = { paths };
    }

    clearPolygon(profileKey: string) {
        delete this.polygons[profileKey];
    }

    /** Used when the profile panel closes or a different datapoint is selected. */
    clearAll() {
        this.polygons = {};
    }

    hasPolygon(profileKey: string): boolean {
        return !!this.polygons[profileKey];
    }
}
